/**
 * Project Card Component
 * Displays a project in the sidebar list
 */

import { Archive, Calendar, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProjectTypeBadge } from './ProjectTypeBadge';
import { WorkflowStepperCompact } from './WorkflowStepper';
import type { ProjectListItem } from '../types';

interface ProjectCardProps {
  project: ProjectListItem;
  isSelected: boolean;
  onClick: () => void;
}

export function ProjectCard({ project, isSelected, onClick }: ProjectCardProps) {
  const formatCurrency = (amount?: number, currency: string = 'CAD') => {
    if (amount === undefined) return '';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const isArchived = !!project.archived_at;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left p-4 border rounded-lg transition-colors',
        isSelected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border hover:border-primary/50 hover:bg-muted/50',
        isArchived && 'opacity-60'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-1.5 font-semibold text-sm">
            {isArchived && <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            Quote #{project.quote_number || project.project_number}
          </div>
          <div className="text-xs text-muted-foreground truncate max-w-[180px]">
            {project.account_name || project.project_name}
          </div>
        </div>
        {project.assigned_to_name && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            <span className="truncate max-w-[80px]">{project.assigned_to_name}</span>
          </div>
        )}
      </div>

      {/* Type Badge */}
      <div className="mb-2">
        <ProjectTypeBadge type={project.type} showIcon={false} className="text-xs" />
      </div>

      {/* Workflow Progress */}
      {project.milestones && project.milestones.length > 0 && (
        <div className="mb-2">
          <WorkflowStepperCompact steps={project.milestones} />
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs">
        {project.scheduled_date && (
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {formatDate(project.scheduled_date)}
          </div>
        )}
        {project.amount !== undefined && (
          <div className="font-semibold text-green-600">
            {formatCurrency(project.amount, project.currency)}
          </div>
        )}
      </div>
    </button>
  );
}
